import cx from 'classnames';
import type { FunctionComponent, MouseEvent } from 'react';
import { useCallback, useEffect } from 'react';

import CalendarIcon from '@atlaskit/icon/core/calendar';
import SpreadsheetIcon from '@atlaskit/icon/core/spreadsheet';
import { boardSwitcherSharedState } from '@trello/board-switcher';
import { RouterLink, type RouterLinkProps } from '@trello/router/router-link';
import { useSharedState, useSharedStateSelector } from '@trello/shared-state';
import type { BoardSwitcherTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { token } from '@trello/theme';

import { IconListTile } from 'app/src/components/BoardTile/IconListTile';
import { focusBoardSwitcherNavItem } from './focusBoardSwitcherNavItem';
import { keyboardNavSharedState } from './keyboardNavSharedState';
import { useSetActiveBoardTileOnHoverOrFocus } from './useSetActiveBoardTileOnHoverOrFocus';

import * as styles from './WorkspaceViewTile.module.less';

interface WorkspaceViewTileProps extends Omit<RouterLinkProps, 'onClick'> {
  id: string;
  type?: string;
  onClick: (e: MouseEvent<HTMLAnchorElement>) => void;
}

export const WorkspaceViewTile: FunctionComponent<WorkspaceViewTileProps> = ({
  id,
  title,
  href,
  type = 'table',
  onClick,
}) => {
  const icon =
    type === 'calendar' ? (
      <CalendarIcon
        label=""
        color={token('color.icon.accent.gray', '#758195')}
      />
    ) : (
      <SpreadsheetIcon
        label=""
        color={token('color.icon.accent.gray', '#758195')}
      />
    );

  const [{ activeId, activeSection }] = useSharedState(keyboardNavSharedState);
  const layout = useSharedStateSelector(
    boardSwitcherSharedState,
    useCallback((value) => value.layout, []),
  );

  const activeStateHandlers = useSetActiveBoardTileOnHoverOrFocus(
    'workspace',
    id,
  );

  const isActive = activeId === id && activeSection === 'workspace';

  useEffect(() => {
    if (isActive) {
      focusBoardSwitcherNavItem('workspace', id);
    }
  }, [id, isActive]);

  if (layout === 'list') {
    return (
      <div
        className={cx(styles.viewTile, {
          [styles.active]: isActive,
        })}
        data-testid={getTestId<BoardSwitcherTestIds>('workspace-view-tile')}
      >
        <IconListTile
          icon={icon}
          href={href}
          className={cx({ [styles.active]: isActive })}
          data-nav-item="view-tile"
          data-section="workspace"
          data-id={id}
          testId={getTestId<BoardSwitcherTestIds>('workspace-view-tile')}
          role="button"
          {...activeStateHandlers}
        >
          {title}
        </IconListTile>
      </div>
    );
  }

  return (
    <div
      className={cx(styles.viewTile, styles.grid, {
        [styles.active]: isActive,
      })}
      data-testid={getTestId<BoardSwitcherTestIds>('workspace-view-tile')}
    >
      <RouterLink
        href={href}
        role="button"
        onClick={onClick}
        data-nav-item="view-tile"
        data-section="workspace"
        data-id={id}
        {...activeStateHandlers}
      >
        <div className={styles.viewDetails}>
          <span className={styles.iconContainer}>{icon}</span>
          <div className={styles.viewNameContainer}>
            <span className={styles.viewName}>{title}</span>
          </div>
        </div>
      </RouterLink>
    </div>
  );
};
